import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Zap,
  Target,
  BarChart3,
  Award,
  BookOpen,
  Users,
  Clock,
  CheckCircle,
  Play,
  ChevronRight,
  Sparkles,
  Shield,
  TrendingUp,
  Brain,
  LineChart,
  Filter,
} from "lucide-react";

const Section2 = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      id: 0,
      title: "Choose Your Test",
      description: "Select from 10,000+ mock tests across 50+ exam categories",
      icon: Target,
      color: "from-blue-500 to-cyan-500",
      features: [
        "Government Exams (SSC, UPSC, Banking)",
        "Competitive Exams (CAT, GATE, JEE)",
        "Corporate Assessments",
        "Academic Tests",
      ],
    },
    {
      id: 1,
      title: "Take AI-Proctored Test",
      description: "Real exam simulation with intelligent proctoring",
      icon: Shield,
      color: "from-purple-500 to-pink-500",
      features: [
        "Real-time monitoring",
        "Adaptive difficulty",
        "Time-bound sections",
        "Question navigation",
      ],
    },
    {
      id: 2,
      title: "Get Detailed Analysis",
      description: "AI-powered performance insights and recommendations",
      icon: Brain,
      color: "from-orange-500 to-red-500",
      features: [
        "Strength & weakness analysis",
        "Time management insights",
        "Comparative ranking",
        "Personalized recommendations",
      ],
    },
    {
      id: 3,
      title: "Track Progress",
      description: "Monitor improvement with comprehensive analytics",
      icon: TrendingUp,
      color: "from-green-500 to-emerald-500",
      features: [
        "Performance trends",
        "All India Ranking",
        "Score prediction",
        "Improvement tracking",
      ],
    },
  ];

  const features = [
    {
      icon: Zap,
      title: "AI-Powered",
      description: "Intelligent question selection based on your performance",
      stat: "98% accuracy",
    },
    {
      icon: Users,
      title: "Live Competition",
      description: "Compete with thousands in real-time mock tests",
      stat: "500+ daily tests",
    },
    {
      icon: Clock,
      title: "Time Analytics",
      description: "Detailed breakdown of time spent per question",
      stat: "35% time saved",
    },
    {
      icon: Award,
      title: "Certification",
      description: "Get certified scores accepted by institutions",
      stat: "1000+ partners",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50 py-20 lg:py-28">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute left-1/4 top-1/4 h-64 w-64 rounded-full bg-gradient-to-r from-blue-500/5 to-purple-500/5 blur-3xl" />
        <div className="absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-gradient-to-r from-cyan-500/5 to-green-500/5 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 px-4 py-2 mb-6"
          >
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-700">
              HOW TESTSETU WORKS
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6"
          >
            Your Path to{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Success Made Simple
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Four simple steps to transform your preparation with intelligent
            technology
          </motion.p>
        </div>

        {/* Steps Navigation */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4">
            {steps.map((step, index) => (
              <motion.button
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setActiveStep(index)}
                className={`group flex items-center gap-3 rounded-xl px-6 py-4 transition-all duration-300 ${
                  activeStep === index
                    ? "bg-white shadow-lg border border-gray-200"
                    : "hover:bg-white/50 hover:shadow-md"
                }`}
              >
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-r ${step.color} ${
                    activeStep === index
                      ? "ring-4 ring-opacity-30"
                      : "group-hover:scale-110"
                  } transition-transform duration-300`}
                >
                  <step.icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-semibold ${
                        activeStep === index
                          ? "text-gray-900"
                          : "text-gray-600"
                      }`}
                    >
                      Step {index + 1}
                    </span>
                    {activeStep === index && (
                      <ChevronRight className="h-4 w-4 text-blue-500" />
                    )}
                  </div>
                  <h3
                    className={`font-bold ${
                      activeStep === index
                        ? "text-gray-900"
                        : "text-gray-700 group-hover:text-gray-900"
                    }`}
                  >
                    {step.title}
                  </h3>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Step Details */}
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-r ${steps[activeStep].color}`}
                >
                  {/* <steps[activeStep].icon className="h-7 w-7 text-white" /> */}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {steps[activeStep].title}
                  </h3>
                  <p className="text-gray-600">{steps[activeStep].description}</p>
                </div>
              </div>

              <div className="space-y-4">
                {steps[activeStep].features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Interactive Demo Card */}
            <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Live Demo</h4>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-sm text-green-600">Active Now</span>
                </div>
              </div>
              <div className="aspect-video rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 mb-4 flex items-center justify-center">
                <button className="group flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-lg hover:shadow-xl transition-shadow">
                  <Play className="h-6 w-6 text-blue-600 group-hover:scale-110 transition-transform" />
                </button>
              </div>
              <p className="text-sm text-gray-600 text-center">
                Watch how {steps[activeStep].title.toLowerCase()} works in real-time
              </p>
            </div>
          </motion.div>

          {/* Right: Features Grid */}
          <div className="space-y-8">
            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600`}
                    >
                      <feature.icon className="h-6 w-6" />
                    </div>
                    <span className="text-lg font-bold text-gray-900">
                      {feature.stat}
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Stats Visualization */}
            <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-6">
              <h4 className="font-bold text-gray-900 mb-6">
                Performance Impact
              </h4>
              <div className="space-y-6">
                {[
                  { label: "Accuracy Improvement", value: 85, color: "bg-gradient-to-r from-blue-500 to-cyan-500" },
                  { label: "Time Management", value: 72, color: "bg-gradient-to-r from-purple-500 to-pink-500" },
                  { label: "Concept Clarity", value: 91, color: "bg-gradient-to-r from-green-500 to-emerald-500" },
                  { label: "Exam Readiness", value: 78, color: "bg-gradient-to-r from-orange-500 to-red-500" },
                ].map((stat, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-700">{stat.label}</span>
                      <span className="font-semibold text-gray-900">
                        {stat.value}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${stat.value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: index * 0.2 }}
                        className={`h-full rounded-full ${stat.color}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Start Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-bold text-lg">Ready to Start?</h4>
                  <p className="text-blue-100 text-sm">
                    Begin your journey in under 2 minutes
                  </p>
                </div>
                <Clock className="h-8 w-8 text-white/80" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-100">Account Setup</span>
                  <span className="font-semibold">30 seconds</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-100">Test Selection</span>
                  <span className="font-semibold">45 seconds</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-100">First Test Started</span>
                  <span className="font-semibold">2 minutes</span>
                </div>
              </div>
              <button className="mt-6 w-full rounded-lg bg-white py-3 font-semibold text-blue-600 hover:bg-blue-50 transition-colors">
                Get Started Now
              </button>
            </motion.div>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="inline-flex flex-wrap items-center justify-center gap-4 rounded-2xl bg-gradient-to-r from-white to-gray-50 border border-gray-200 px-8 py-6">
            <div className="text-left">
              <h4 className="font-bold text-gray-900">
                Still have questions about how it works?
              </h4>
              <p className="text-gray-600">
                Our experts are ready to guide you personally
              </p>
            </div>
            <div className="flex gap-3">
              <button className="rounded-lg border border-blue-600 bg-white px-6 py-3 font-semibold text-blue-600 hover:bg-blue-50">
                Book a Demo
              </button>
              <button className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-semibold text-white hover:shadow-lg">
                Talk to Expert
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Section2;